import { Component, OnDestroy, OnInit, QueryList, ViewChildren } from '@angular/core';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import moment from 'moment';
import { Subject, takeUntil } from 'rxjs';
import { AuthService } from '../../../auth/auth.service';
import { UserDto } from '../../../auth/user.dto';
import { Pagination } from '../../../shared/pagination/pagination';
import { PaginationOptions } from '../../../shared/pagination/pagination-options';
import { BeneficiariesService } from '../../../shared/services/beneficiaries.service';
import { DateService } from '../../../shared/services/date.service';
import { RSJService } from '../../../shared/services/rsj.service';
import { SortDirection, SortEvent } from '../../../shared/sortable/sort-event';
import { SortableDirective } from '../../../shared/sortable/sortable.directive';
import { ToastService } from '../../../shared/toast/toast.service';
import { BeneficiaryDto } from '../../dto/beneficiary.dto';
import { RsjInstructionDisplayDto } from './dto/rsj-instruction-display.dto';
import { RsjInstructionDto } from './dto/rsj-instruction.dto';
import { RSJDetailsComponent } from './rsj-instruction-details/rsj-instruction-details.component';
import { UserService } from '../../../shared/services/user.service';

@Component({
  selector: 'app-rsj',
  templateUrl: './rsj-instruction.component.html',
  standalone: false,
})
export class RsjInstructionComponent implements OnInit, OnDestroy {
  public beneficiary: BeneficiaryDto;
  public canCreate: boolean = true;
  protected readonly destroy$: Subject<void> = new Subject();

  public ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
  public user: UserDto;
  private static readonly resetInstructionRSJ: Pagination<RsjInstructionDisplayDto> = new Pagination([], 0, 0, 1, new PaginationOptions(1, 25));

  constructor(
    private readonly authService: AuthService,
    private readonly modalService: NgbModal,
    private readonly toastService: ToastService,
    private readonly beneficiariesService: BeneficiariesService,
    private readonly rsjService: RSJService,
    private readonly dateService: DateService,
    private readonly userService: UserService,
  ) {}

  @ViewChildren(SortableDirective) headers: QueryList<SortableDirective>;
  public instructionsRSJ: Pagination<RsjInstructionDisplayDto> = RsjInstructionComponent.resetInstructionRSJ;

  public async ngOnInit(): Promise<void> {
    this.user = await this.authService.getUser();
    this.beneficiariesService.beneficiary.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe((beneficiary: BeneficiaryDto) => (this.beneficiary = beneficiary));

    await this.checkCanCreate();
    await this.updateInstructionsRSJ(this.instructionsRSJ.options);

    this.rsjService.instructionRSJCreated$.pipe(takeUntil(this.destroy$)).subscribe(() => this.updateInstructionsRSJ(this.instructionsRSJ.options));
  }

  public async checkCanCreate() {
    let paymentData = await this.rsjService.getPayments(this.beneficiary?.id, new PaginationOptions(1, 25));
    let age = moment(this.dateService.getCurrent()).diff(this.beneficiary.birthDate, 'years');
    if (!(this.user.groups.includes('ADMIN') || this.user.groups.includes('STRUCTURE')) || age >= 25 || paymentData.totalItems >= 24) {
      this.canCreate = false;
    }
  }

  private async updateInstructionsRSJ(pagination: PaginationOptions): Promise<void> {
    this.instructionsRSJ = await this.rsjService.findInstructionsByBeneficiaryId(this.beneficiary.id, pagination);
  }

  public async openCreateModal(): Promise<void> {
    if (!(await this.userService.getSignature())) {
      this.toastService.show('Veuillez enregistrer une signature dans votre profil', 'danger');
      return;
    }
    if (!this.user?.groups?.some((group: string) => ['ADMIN', 'STRUCTURE', 'DIRECTION+'].includes(group))) {
      this.toastService.show("Vous n'avez pas l'autorisation d'accéder à cette page.", 'danger');
      return;
    }
    try {
      const instructionRSJInit: RsjInstructionDto = await this.rsjService.getInstructionRSJInit(this.beneficiary.id);
      this.openModal(instructionRSJInit);
    } catch (error) {
      if (error.status === 461) {
        this.toastService.show('Veuillez compléter le dossier du bénéficiaire (Accompagnement, Orientation, Référent, Adresse...).', 'danger');
      }
    }
  }

  public async openConsultModal(instructionId: number): Promise<void> {
    if (this.user?.groups?.some((group: string) => ['ADMIN', 'STRUCTURE', 'DIRECTION+'].includes(group))) {
      try {
        const instructionRSJ: RsjInstructionDto = await this.rsjService.getInstructionRSJ(instructionId);
        this.openModal(instructionRSJ);
      } catch (error) {
        if (error.status === 461) {
          this.toastService.show('Veuillez compléter toutes les informations relatives au RSJ dans le dossier du bénéficiaire.', 'danger');
        } else if (error.status === 437) {
          this.toastService.show("Vous n'avez pas l'autorisation de consulter cette instruction RSJ.", 'danger');
        }
      }
    } else {
      this.toastService.show("Vous n'avez pas l'autorisation d'accéder à cette page.", 'danger');
    }
  }

  public async onSort(event: SortEvent): Promise<void> {
    // resetting other headers
    const column: string = event.column;
    const direction: SortDirection = event.direction;
    this.headers.forEach((header: SortableDirective) => {
      if (header.sortable !== column) {
        header.direction = '';
      }
    });
    await this.updateInstructionsRSJ(this.instructionsRSJ.options.withSort(column, direction == 'desc'));
  }

  public async changePage(pageNb?: number): Promise<void> {
    if (pageNb) {
      await this.updateInstructionsRSJ(this.instructionsRSJ.options.withPage(pageNb));
    }
  }

  public async changeLimit(event: any): Promise<void> {
    if (event && event.target && event.target.value) {
      const limit: number = event.target.value;
      await this.updateInstructionsRSJ(this.instructionsRSJ.options.withLimit(limit));
    }
  }

  private openModal(instructionRSJ: RsjInstructionDto): void {
    const modal: NgbModalRef = this.modalService.open(RSJDetailsComponent, {
      windowClass: 'rightModal',
      size: 'xl',
      backdrop: 'static',
    });
    const modalComponent: RSJDetailsComponent = modal.componentInstance as RSJDetailsComponent;
    modalComponent.onInit(instructionRSJ, this.user);
  }
}
